import type { TableMenuItem } from '@/components'
import type { TableMenuItem as OldTableMenuItem } from '@/components_deprecated/Table'
import { Module, ModuleOptionsValue } from '@/modules/companies'
import { STAGE } from '@/utils'
import {
  useAdmin,
  useConfirm,
  useModuleConfig,
  useModuleResponsible,
  usePermissions,
  useLeaderGroups,
} from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { getFullMinutes } from '@/utils/time'
import { usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { subSeconds } from 'date-fns'
import type { Dispatch, SetStateAction } from 'react'
import { useState } from 'react'
import { isMobileOnly } from 'react-device-detect'
import { useTimeEntryTableHooks } from '../mutations.hooks'
import type {
  EditAbsenceForm,
  EditAbsenceStatusForm,
  TimeEntryFormValues,
} from '../types'
import type {
  TimesheetsDayReport,
  TimesheetsDayReportAbsence,
  TimesheetsDayReportTimeEntry,
  TimesheetsSalaryWorkTime,
} from '../types.graphql'
import type { AbsenceDialogRef } from './absence-dialog'
import type { AbsenceNode } from './absences-table'
import type { EditableAbsence } from './EditAbsenceModal'
import { EditAbsenceModal } from './EditAbsenceModal'
import { EditAbsenceStatusModal } from './EditAbsenceStatusModal'
import { EditTimeEntryModal } from './EditTimeEntryModal'
import { ViewCommentModal } from './ViewCommentModal'

export function useTimesheetHandlers(salaryWorkTime: TimesheetsSalaryWorkTime) {
  const confirm = useConfirm()
  const addPrompt = usePrompt()
  const [entryLoading, setEntryLoading] = useState<string | null>(null)
  const translations = useTranslate({
    editTimeEntry: 'timesheets.edit-time-entry',
    deleteTimeEntry: 'timesheets.delete-time-entry',
    editAbsence: 'timesheets.edit-absence',
    editAbsenceStatus: 'timesheets.edit-absence-status',
    deleteAbsence: 'timesheets.delete-absence',
    viewComment: 'common.view-comment',
    noComment: 'common.no-comment',
    prompt: {
      deleteTimeEntry: 'timesheets.prompt.delete-time-entry',
      deleteTimeEntryTitle: 'timesheets.prompt.delete-time-entry-title',
      deleteAbsence: 'timesheets.prompt.delete-absence',
      deleteAbsenceTitle: 'timesheets.prompt.delete-absence-title',
    },
  })
  const admin = useAdmin()

  const { hasPermissionsAndMe, hasPermissions } = usePermissions()

  const canEdit = (id: string, type: 'timeentry' | 'absence') =>
    admin || hasPermissionsAndMe({ id }, PERMISSIONS.timesheets.change[type])
  const canDelete = (id: string, type: 'timeentry' | 'absence') =>
    admin || hasPermissionsAndMe({ id }, PERMISSIONS.timesheets.delete[type])

  const {
    patchTimeEntry,
    deleteTimeEntry,
    loading: mutationLoading,
  } = useTimeEntryTableHooks()

  async function handleViewComment(comment: string) {
    await addPrompt<boolean | null>(resolve => (
      <ViewCommentModal onClose={resolve} comment={comment} />
    ))
  }
  const { getModuleOptionStatus } = useModuleConfig(Module.TIMESHEETS)
  const overtimeCustomChoice = getModuleOptionStatus(
    ModuleOptionsValue.TIMESHEETS_OVERTIME_OPTIONS
  )

  /* Time Entry Handlers */
  const handleEditTimeEntry = async (
    entry: TimesheetsDayReportTimeEntry,
    modalOpenCallback?: Dispatch<SetStateAction<boolean>>,
    existingTimeEntries: TimesheetsDayReportTimeEntry[] = []
  ) => {
    const canSetSalaryCodeManually = hasPermissions(
      PERMISSIONS.timesheets.overrideTimeEntrySalaryCode,
      false
    )
    modalOpenCallback?.(true)
    const { data } = await addPrompt<TimeEntryFormValues | null>(resolve => (
      <EditTimeEntryModal
        entry={entry}
        onResolve={resolve}
        salaryWorkTime={salaryWorkTime}
        overtimeCustomChoice={overtimeCustomChoice}
        existingTimeEntries={existingTimeEntries}
        canSetSalaryCodeManually={canSetSalaryCodeManually}
      />
    ))
    modalOpenCallback?.(false)
    if (!data) return

    setEntryLoading(entry.id)
    await patchTimeEntry({
      variables: {
        id: entry.id,
        input: {
          datetimeStart: data.start.toISOString(),
          datetimeEnd: data.end.toISOString(),
          project: data.project,
          comment: data.comment,
          salaryCodeOvertime: data.overtimeSalaryCodeId,
          salaryCodeOrdinary: data.salaryCodeId,
          diet: data.diet,

          department: data.department,
          pause: getFullMinutes(data.pause),
        },
      },
    }).finally(() => setEntryLoading(null))
  }

  /* Shared Handlers */
  const handleDeleteTimeEntry = async (
    entry: TimesheetsDayReportTimeEntry,
    modalOpenCallback?: Dispatch<SetStateAction<boolean>>
  ) => {
    const promptMessage = translations.prompt.deleteTimeEntry
    const promptTitle = translations.prompt.deleteTimeEntryTitle

    modalOpenCallback?.(true)
    const { data: answer } = await confirm(promptMessage, promptTitle)
    modalOpenCallback?.(false)
    if (!answer) return

    setEntryLoading(entry.id)
    await deleteTimeEntry({
      variables: { id: entry.id },
    }).finally(() => setEntryLoading(null))
  }

  function createTimesheetDayReportMenuItems(dayReport: TimesheetsDayReport) {
    if (
      dayReport.timeEntries.length + dayReport.absences.length > 1 ||
      dayReport.absences.length === 1 ||
      dayReport.timeEntries.length + dayReport.absences.length === 0
    )
      return []
    const isAbsence =
      dayReport.timeEntries.length === 0 && dayReport.absences.length === 1

    if (isAbsence) return []
    const items: OldTableMenuItem<undefined>[] = []

    const canViewComment = (entry: TimesheetsDayReportTimeEntry) =>
      entry.comment === '' || entry.comment === translations.noComment

    const entry = dayReport.timeEntries[0]
    if (!entry) return []

    if (canEdit(entry.user.id, 'timeentry')) {
      items.push({
        label: translations.editTimeEntry,
        role: 'link',
        onClick: () => handleEditTimeEntry(entry),
      })
    }
    if (canViewComment(entry) && isMobileOnly) {
      items.push({
        label: translations.viewComment,
        onClick: () => handleViewComment(entry.comment),
      })
    }
    if (canDelete(entry.user.id, 'timeentry')) {
      items.push({
        label: translations.deleteTimeEntry,
        onClick: () => handleDeleteTimeEntry(entry),
      })
    }

    return items
  }

  function createTimesheetTimeEntryMenuItems(
    entry: TimesheetsDayReportTimeEntry,
    existingTimeEntries: TimesheetsDayReportTimeEntry[] = []
  ) {
    const items: OldTableMenuItem<undefined>[] = []

    const canViewComment = (entry: TimesheetsDayReportTimeEntry) =>
      entry.comment === '' || entry.comment === translations.noComment

    if (canEdit(entry.user.id, 'timeentry')) {
      items.push({
        label: translations.editTimeEntry,
        role: 'link',
        onClick: () =>
          handleEditTimeEntry(entry, undefined, existingTimeEntries),
      })
    }
    if (canViewComment(entry) && isMobileOnly) {
      items.push({
        label: translations.viewComment,
        onClick: () => handleViewComment(entry.comment),
      })
    }
    if (canDelete(entry.user.id, 'timeentry')) {
      items.push({
        label: translations.deleteTimeEntry,
        onClick: () => handleDeleteTimeEntry(entry),
      })
    }
    return items
  }

  return {
    timeEntryHandler: {
      edit: handleEditTimeEntry,
      createTimesheetTimeEntryMenuItems,
    },
    timesheetHandler: {
      createTimesheetDayReportMenuItems,
      delete: handleDeleteTimeEntry,
      viewComment: handleViewComment,
    },
    entryLoading,
    mutationLoading,
  }
}

/** @deprecated use useAbsenceHandler */
export function useOldAbsenceHandler(
  absenceDialogRef?: React.RefObject<AbsenceDialogRef | null>
) {
  const translations = useTranslate({
    open: 'common.open',
    editAbsence: 'timesheets.edit-absence',
    editAbsenceStatus: 'timesheets.edit-absence-status',
    deleteAbsence: 'timesheets.delete-absence',
    prompt: {
      deleteAbsence: 'timesheets.prompt.delete-absence',
      deleteAbsenceTitle: 'timesheets.prompt.delete-absence-title',
    },
  })

  const confirm = useConfirm()
  const addPrompt = usePrompt()
  const admin = useAdmin()
  const { isResponsibleUser } = useModuleResponsible(Module.ABSENCES)
  const { hasAccess, myLeaderGroups, groupsLoading } = useLeaderGroups()

  const { hasPermissions } = usePermissions()

  const canEdit = (_id: string, type: 'timeentry' | 'absence') =>
    admin || hasPermissions(PERMISSIONS.timesheets.change[type])
  const canDelete = (_id: string, type: 'timeentry' | 'absence') =>
    admin || hasPermissions(PERMISSIONS.timesheets.delete[type])

  const {
    patchAbsence,
    deleteAbsence,
    loading: mutationLoading,
  } = useTimeEntryTableHooks()

  const handleEditAbsence = async (
    absence: EditableAbsence,
    modalOpenCallback?: Dispatch<SetStateAction<boolean>>
  ) => {
    modalOpenCallback?.(true)
    const { data } = await addPrompt<EditAbsenceForm | null>(resolve => (
      <EditAbsenceModal absence={absence} onResolve={resolve} />
    ))
    modalOpenCallback?.(false)
    if (!data) return

    await patchAbsence({
      variables: {
        id: absence.id,
        input: {
          datetimeStart: data.start.toISOString(),
          datetimeEnd: data.end.toISOString(),
          reason: data.reason,
          absenceType: data.absenceType,
        },
      },
    })
  }

  function createAbsenceMenuItems(
    absence: AbsenceNode,
    absenceView: boolean
  ): TableMenuItem[] {
    const items: TableMenuItem[] = []

    items.push({
      type: 'link',
      id: 'open-absence',
      label: translations.open,
      to: `/absences/${absence.id}`,
      target: '_blank',
    })

    const canEditAbsenceStatus =
      admin ||
      isResponsibleUser ||
      (myLeaderGroups
        .flatMap(g => g.users)
        .some(u => u.id === absence.user.id) &&
        !groupsLoading &&
        hasAccess)

    if (canEditAbsenceStatus) {
      if (canEdit(absence.user.id, 'absence')) {
        if (absenceView) {
          items.push({
            type: 'regular',
            id: 'edit-absence-status',
            label: translations.editAbsenceStatus,
            onClick: () => handleEditAbsenceStatus(absence),
          })
        }

        items.push({
          type: 'regular',
          id: 'edit-absence',
          label: translations.editAbsence,
          onClick: async () => {
            if (STAGE === 'production') await handleEditAbsence(absence)
            else
              absenceDialogRef?.current?.open({
                ...absence,
                user: absence.user.id,
                absenceType: absence.absenceType?.id || '',
                datetimeStart: new Date(absence.datetimeStart),
                datetimeEnd: subSeconds(new Date(absence.datetimeEnd), 1),
              })
          },
        })
      }

      if (canDelete(absence.user.id, 'absence')) {
        items.push({
          type: 'regular',
          id: 'delete-absence',
          label: translations.deleteAbsence,
          onClick: () => handleDeleteAbsence(absence),
        })
      }
    }

    return items
  }

  function old_createAbsenceMenuItems(
    absence: EditableAbsence,
    absenceView: boolean = true
  ) {
    const items: OldTableMenuItem<undefined>[] = []

    const canEditAbsenceStatus =
      admin ||
      isResponsibleUser ||
      (myLeaderGroups
        .flatMap(g => g.users)
        .some(u => u.id === absence.user.id) &&
        !groupsLoading &&
        hasAccess)

    if (canEditAbsenceStatus) {
      if (canEdit(absence.user.id, 'absence')) {
        if (absenceView) {
          items.push({
            label: translations.editAbsenceStatus,
            role: 'link',
            onClick: () => handleEditAbsenceStatus(absence),
          })
        }

        items.push({
          label: translations.editAbsence,
          role: 'link',
          onClick: () => handleEditAbsence(absence),
        })
      }

      if (canDelete(absence.user.id, 'absence')) {
        items.push({
          label: translations.deleteAbsence,
          onClick: () => handleDeleteAbsence(absence),
        })
      }
    }

    return items
  }

  async function handleDeleteAbsence(
    absence: Pick<TimesheetsDayReportAbsence, 'id'>,
    modalOpenCallback?: Dispatch<SetStateAction<boolean>>
  ) {
    const promptMessage = translations.prompt.deleteAbsence
    const promptTitle = translations.prompt.deleteAbsenceTitle

    modalOpenCallback?.(true)
    const { data: answer } = await confirm(promptMessage, promptTitle)
    modalOpenCallback?.(false)
    if (!answer) return

    await deleteAbsence({
      variables: { id: absence.id },
    })
  }

  async function handleEditAbsenceStatus(
    absence: Pick<TimesheetsDayReportAbsence, 'id' | 'requestStatus'>,
    modalOpenCallback?: Dispatch<SetStateAction<boolean>>,
    refetch?: () => Promise<any>
  ) {
    modalOpenCallback?.(true)
    const { data } = await addPrompt<EditAbsenceStatusForm | null>(resolve => (
      <EditAbsenceStatusModal absence={absence} onSubmit={resolve} />
    ))
    modalOpenCallback?.(false)
    if (!data) return

    await patchAbsence({
      variables: {
        id: absence.id,
        input: data,
      },
    })
    await refetch?.()
  }

  return {
    edit: handleEditAbsence,
    editStatus: handleEditAbsenceStatus,
    delete: handleDeleteAbsence,
    createAbsenceMenuItems,
    old_createAbsenceMenuItems,
    mutationLoading,
  }
}
